import React from "react";
import { ArrowRight, Gamepad2, Headphones, Star, Sparkles } from "lucide-react";
import { LINKS } from "@/lib/brand";

const team = [
  {
    name: "Paula",
    role: "Rettungssanitäterin",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/vxyrztvr_file_000000007b4071f6bc9cccab648b6dcc.png",
    href: "https://notfall-werkstatt.emergent.host/erste-hilfe",
    accent: "bg-paula-red",
  },
  {
    name: "Paul",
    role: "Feuerwehr",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/ufctngs1_openart-22b5188a05b54bba9805e42426b3ef94_00001__raw.png",
    href: "https://notfall-werkstatt.emergent.host/brandschutz",
    accent: "bg-paula-yellow",
  },
  {
    name: "Peter",
    role: "Polizist",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/se681tjl_file_00000000f71c7246b166c850c68e4556_edit_285000589227865.jpg",
    href: "https://notfall-werkstatt.emergent.host/sicherheit",
    accent: "bg-paula-blue",
  },
  {
    name: "Piet",
    role: "Luftrettung",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/4y8fqhr6_Screenshot_20260530_154910_com.openai.chatgpt.jpg",
    href: "https://notfall-werkstatt.emergent.host/luftrettung",
    accent: "bg-paula-ink",
  },
  {
    name: "Pia",
    role: "Bevölkerungsschutz",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/8g2knlmo_file_00000000b480720aa662f07fd818b403.png",
    href: "https://notfall-werkstatt.emergent.host/bevoelkerungsschutz",
    accent: "bg-paula-red",
  },
];

const features = [
  {
    icon: Gamepad2,
    title: "Minispiele",
    text: "Quiz, Notfallrucksack packen, Finde die Maus, Notruf üben.",
    color: "bg-paula-yellow-soft",
    iconColor: "text-paula-yellow",
  },
  {
    icon: Star,
    title: "Mutsterne sammeln",
    text: "Abzeichen verdienen und ein echter Rettungs-Profi werden.",
    color: "bg-paula-red-soft",
    iconColor: "text-paula-red",
  },
  {
    icon: Headphones,
    title: "Hörspiele & Filme",
    text: "Blaulicht mit ❤, Hand in Hand, Bleib cool — Geschichten zum Mitfiebern.",
    color: "bg-paula-blue-soft",
    iconColor: "text-paula-blue",
  },
];

export const AppShowcase = () => {
  return (
    <section
      id="app"
      data-testid="app-section"
      className="relative py-24 sm:py-32 bg-paula-cream overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full bg-paula-yellow-soft blur-3xl opacity-70"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-20 w-[460px] h-[460px] rounded-full bg-paula-blue-soft blur-3xl opacity-60"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-paula-ink/80 mb-5">
              <Sparkles size={14} className="text-paula-yellow" />
              Die Notfall-Werkstatt · Web-App
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-paula-ink leading-[1.05] tracking-tight">
              Paulas Welt geht{" "}
              <span className="text-paula-red">weiter</span> — mit dem
              ganzen P-Team.
            </h2>
            <p className="mt-6 text-paula-muted text-lg leading-relaxed max-w-2xl">
              5 Pinguine, 5 Themen, ein Versprechen: Kinder von 6–12 Jahren lernen
              spielerisch, mutig zu helfen. Direkt im Browser — kein Download
              nötig.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <a
              href={LINKS.app}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-cta-launch"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-paula-ink text-white font-semibold hover:bg-paula-red transition-colors"
            >
              App jetzt starten
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>

        {/* P-Team Karten */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {team.map((m) => (
            <a
              key={m.name}
              href={m.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`p-team-${m.name.toLowerCase()}`}
              className="group relative rounded-3xl bg-white border border-slate-200 hover:border-paula-ink transition-all hover:-translate-y-1 p-5 flex flex-col items-center text-center"
            >
              <div className="relative w-full aspect-square rounded-2xl bg-paula-cream overflow-hidden mb-4">
                <img
                  src={m.img}
                  alt={`Pinguin ${m.name}`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span
                  className={`absolute top-2 left-2 ${m.accent} text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full`}
                >
                  {m.role.split(" ")[0]}
                </span>
              </div>
              <div className="font-display font-bold text-xl text-paula-ink">
                Pinguin {m.name}
              </div>
              <div className="text-xs text-paula-muted mt-0.5">{m.role}</div>
              <div className="mt-3 text-xs font-semibold text-paula-red opacity-0 group-hover:opacity-100 transition-opacity">
                Los geht's →
              </div>
            </a>
          ))}
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`app-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="p-7 rounded-3xl bg-white border border-slate-200"
            >
              <div
                className={`w-12 h-12 rounded-2xl ${f.color} grid place-items-center mb-5`}
              >
                <f.icon className={f.iconColor} size={22} />
              </div>
              <div className="font-display font-bold text-xl text-paula-ink">
                {f.title}
              </div>
              <p className="text-paula-muted text-sm mt-2 leading-relaxed">
                {f.text}
              </p>
            </div>
          ))}
        </div>

        {/* Quick action chips */}
        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={LINKS.appSpiele}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-link-spiele"
            className="px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-paula-ink text-sm font-semibold text-paula-ink transition-colors"
          >
            🎮 Spiele
          </a>
          <a
            href={LINKS.appHoerspiele}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-link-hoerspiele"
            className="px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-paula-ink text-sm font-semibold text-paula-ink transition-colors"
          >
            🎧 Hörspiele
          </a>
          <a
            href={LINKS.appCharta}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-link-charta"
            className="px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-paula-ink text-sm font-semibold text-paula-ink transition-colors"
          >
            📜 P-Team Charta
          </a>
          <a
            href="https://notfall-werkstatt.emergent.host/vortrag"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-link-vortrag"
            className="px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-paula-ink text-sm font-semibold text-paula-ink transition-colors"
          >
            🎤 Vortrag starten
          </a>
        </div>

        <blockquote className="mt-12 max-w-2xl text-paula-muted italic">
          „Mutig sein heißt nicht, keine Angst zu haben — mutig sein heißt, es
          trotzdem zu tun."
        </blockquote>
      </div>
    </section>
  );
};

export default AppShowcase;
